'use client';
import { useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';

export default function DMCAPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <SectionHeader
              eyebrow="Legal"
              title="DMCA Notice & Takedown Procedure"
              description="Last updated: February 18, 2026"
              align="center"
              className="mb-16"
            />
          </div>
        </section>

        <section className="py-24 px-6">
          <div className="max-w-[900px] mx-auto">
            <div className="space-y-12">
              {/* Introduction */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  Best Odds Corp abides by the federal Digital Millennium Copyright Act (DMCA) by responding to notices of alleged infringement that comply with the DMCA and other applicable laws. As part of our response, we may remove or disable access to material residing on a site that is controlled or operated by Best Odds Corp that is claimed to be infringing, in which case we will make a good-faith attempt to contact the person who submitted the affected material so that they may make a counter notification.
                </p>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  Best Odds Corp does not control content hosted on third party websites, and cannot remove content from sites it does not own or control. If you are the copyright owner of content hosted on a third party site, and you have not authorized the use of your content, please contact the administrator of that website directly to have the content removed.
                </p>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  Before serving either a Notice of Infringing Material or Counter-Notification, you may wish to contact a lawyer to better understand your rights and obligations under the DMCA and other applicable laws. The following notice requirements are intended to comply with Best Odds Corp's rights and obligations under the DMCA and, in particular, section 512(c), and do not constitute legal advice.
                </p>
              </div>

              {/* Agent Contact Information */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <div className="text-micro uppercase tracking-ultra-wide text-primary mb-4">Contact Information</div>
                <h2 className="text-2xl font-bold mb-6">Agent Contact Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">By Mail:</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">
                      Attn: Best Odds Corp<br />
                      4575 Dean Martin Dr. Unit 1501<br />
                      Las Vegas, NV 89141<br />
                      USA
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Email:</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">
                      Use our contact form located at <Link href="/contact" className="text-primary hover:underline">contact page</Link>
                    </p>
                  </div>
                  
                  <p className="text-body-sm text-muted-foreground italic mt-4">
                    Note: Submissions by email will normally receive the fastest review.
                  </p>
                </div>
              </div>

              {/* Notice of Infringing Material */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <div className="text-micro uppercase tracking-ultra-wide text-primary mb-4">Filing a Claim</div>
                <h2 className="text-2xl font-bold mb-6">Notice of Infringing Material</h2>
                
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  The Digital Millennium Copyright Act specifies that all infringement claims must be in writing (either electronic mail or paper letter) and must include the following elements:
                </p>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">A physical or electronic signature;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">Identification of the infringed work;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">Identification of the infringed material;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">Contact information for the complainant, e.g. address, telephone number, electronic mail address;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">A statement that the complaining party has a good faith belief that use of the material in the manner complained of is not authorized by the copyright owner or the law; and</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">A statement that the information contained in the notification is accurate, and under penalty of perjury, that the complaining party is authorized to act on behalf of the copyright owner.</span>
                  </li>
                </ul>
              </div>

              {/* Counter-Notification */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <div className="text-micro uppercase tracking-ultra-wide text-primary mb-4">Responding to a Claim</div>
                <h2 className="text-2xl font-bold mb-6">Counter-Notification</h2>
                
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  If material that you have posted to a site controlled or operated by Best Odds Corp has been taken down, you may file a counter-notification that contains the following details:
                </p>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">Identification of the material that has been removed or to which access has been disabled and the location at which the material appeared before it was removed or disabled;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">A statement, under penalty of perjury, that you have a good faith belief that the material was removed or disabled as a result of mistake or misidentification of the material in question;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">Your name, address and telephone number;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">A statement that you consent to the jurisdiction of the Federal District Court for judicial district in which your address is located or, if your address is outside of the USA, for any judicial district in which Best Odds Corp may be found and that you will accept service of process from the person who submitted a notice in compliance with the section (c)(1)(C) of the DMCA, as generally described above; and</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span className="text-body-sm text-muted-foreground">Your physical or electronic signature.</span>
                  </li>
                </ul>
                
                <p className="text-body-sm text-muted-foreground leading-relaxed mt-6">
                  You may be able to find examples of counter-notifications at www.chillingeffects.org/dmca/counter512.pdf. Please note, however, that this is no substitute for legal advice and you should obtain legal advice to better understand your rights and obligations under the DMCA and applicable laws.
                </p>
              </div>

              {/* Attribution */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <div className="text-micro uppercase tracking-ultra-wide text-primary mb-4">Attribution</div>
                <h2 className="text-2xl font-bold mb-6">Sources</h2>
                
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  This DMCA Notice and Takedown procedure page was based on <a href="https://archives.iupui.edu/copyright" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://archives.iupui.edu/copyright</a> and <a href="http://creativecommons.org/dmca" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">http://creativecommons.org/dmca</a>.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}